import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { systemStateService } from '@/features/ai-agent/services/systemStateService';
import type { SystemState } from '@/features/ai-agent/types/quickActions';

interface UseSystemStateQueryOptions {
  /**
   * Enable automatic refetching on an interval
   * @default true
   */
  enableAutoRefresh?: boolean;
  /**
   * Interval for automatic refetching (in milliseconds)
   * @default 60000 (60 seconds)
   */
  refetchInterval?: number;
  /**
   * How long data is considered fresh (in milliseconds)
   * @default 30000 (30 seconds)
   */
  staleTime?: number;
}

/**
 * TanStack Query hook for system state
 * 
 * Replaces the old useSystemState hook with proper caching, automatic refetching,
 * and unified error handling.
 * 
 * @example
 * ```tsx
 * const { data: systemState, isLoading, error, refetch } = useSystemStateQuery();
 * 
 * if (systemState?.overdueTasks > 0) {
 *   // Show urgent actions
 * }
 * ```
 */
export function useSystemStateQuery(options: UseSystemStateQueryOptions = {}) {
  const {
    enableAutoRefresh = true,
    refetchInterval = 60000, // 60 seconds
    staleTime = 30000, // 30 seconds
  } = options;

  const { user } = useAuth();

  return useQuery<SystemState | null, Error>({
    // Query key includes userId to prevent data leaking between users
    queryKey: ['systemState', user?.id],

    // Query function - fetches from Supabase
    queryFn: async () => {
      if (!user?.id) {return null;}
      return systemStateService.getSystemState(user.id);
    },

    // Only run query if user is authenticated
    enabled: !!user?.id,

    // Cache configuration
    staleTime, // Data is fresh for 30s by default
    gcTime: 5 * 60 * 1000, // Keep unused data for 5 minutes (formerly cacheTime)

    // Automatic refetching
    refetchInterval: enableAutoRefresh ? refetchInterval : false,
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchOnReconnect: true, // Refetch when network reconnects

    // Placeholder data while loading (safe defaults)
    placeholderData: {
      overdueTasks: 0,
      tasksDueToday: 0,
      upcomingEvents: 0,
      calendarSyncStatus: 'healthy',
      unreadNotifications: 0,
      recentActivity: [],
    },

    // Error handling - retry 3 times with exponential backoff
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    // Log errors for debugging
    meta: {
      errorMessage: 'Failed to fetch system state',
    },
  });
}

/**
 * Hook to get just the system state data (no loading/error states)
 * Useful when you only need the data and have a parent handling loading states
 */
export function useSystemStateData(): SystemState | null {
  const { data } = useSystemStateQuery();
  return data ?? null;
}
